import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { useConfiguration } from '../../../context/configurationContext';
import { useIntl, FormattedMessage } from '../../../util/reactIntl';
import { createSlug } from '../../../util/urlHelpers';
import {
  canSeeProviderName,
  providerDisplayName,
  providerDisplayLocation,
} from '../../../util/providerAcronym';
import { selectContactedListingIds } from '../../../ducks/contactedProviders.duck';
import { translatedListingFields } from '../../../extensions/empavision-enrichments/packages/ext-multilanguage/util/listingTranslations';

import { NamedLink, IconLocation, IconReviewStar } from '../../../components';

import medalIcon from './medal.svg';
import css from './ProviderCard.module.css';

// The design's card shows enum labels, not raw option values; resolve them
// through the hosted listing-field config so Console renames flow through.
const labelForOption = (listingFields, fieldKey, optionValue) => {
  const field = listingFields?.find(f => f.key === fieldKey);
  const enumOption = field?.enumOptions?.find(o => `${o.option}` === `${optionValue}`);
  return enumOption?.label || optionValue;
};

/**
 * Search-results card for service providers (text card, no listing image).
 * Every block renders conditionally so listings with partial publicData
 * degrade gracefully instead of showing empty holes.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {Object} props.listing listing entity (search results include attributes only)
 * @param {Function?} props.setActiveListing
 * @returns {JSX.Element}
 */
export const ProviderCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const { className, rootClassName, listing, setActiveListing } = props;

  const classes = classNames(rootClassName || css.root, className);
  const id = listing?.id?.uuid;
  const { publicData = {}, metadata = {} } = listing?.attributes || {};
  // Stored FR translations (publicData.translations) when browsing in French.
  const { title: fullTitle, description } = translatedListingFields(listing);

  // Read straight from the store rather than threading the viewer's inquiries
  // through SearchResultsPanel, which has no other reason to know about them.
  // This card is SearchPage-private, and the pages in this directory already
  // use useSelector.
  const contactedListingIds = useSelector(selectContactedListingIds);

  // The server renders anonymously, so revealing a name during the first client
  // render would be a hydration mismatch on every card. Defer the reveal to
  // after mount — the same guard ListingPage.shared.js uses, and the idiom
  // TopbarDesktop already follows for authenticated-only output.
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);

  // The provider's company name is the listing title, so a viewer who has not
  // inquired sees the acronym here — this card is the surface the client
  // screenshotted. Fetching is left to the page: SearchResultsPanel renders
  // many of these, and the thunk's `condition` already de-duplicates, but not
  // dispatching from a list item keeps the request count obvious.
  // No `isOwnListing` exemption here, deliberately. Everywhere else a provider
  // sees their own real name (their profile page, Manage profile, the wizard),
  // but search results are the shop window: the client reported their own card
  // as a bug because it was the one card not showing what a visitor sees.
  // Browsing the directory is not managing your profile, so this surface shows
  // every provider — the viewer included — exactly as a visitor sees them.
  const canSeeFullName =
    mounted && canSeeProviderName({ listingId: id, contactedListingIds });

  const title = providerDisplayName({ companyName: fullTitle, canSeeFullName });

  // Slug follows the displayed title, so a locked viewer's links read
  // /l/afsc/<id> rather than /l/accra-food-safety-consulting-ltd/<id>. The slug
  // segment is decorative — the route resolves on the id alone — so this is
  // safe and keeps the name out of anything the viewer copies or shares.
  const slug = createSlug(title || '');

  const {
    categoryLevel1,
    location: publicLocation,
    country,
    serviceAreas,
    services,
    establishedYear,
    foodBusinessesServed,
  } = publicData;

  const categories = config.categoryConfiguration?.categories || [];
  const category = categories.find(c => c.id === categoryLevel1);
  // Until hosted categories are configured in Console, fall back to a
  // prettified raw value so the badge still renders.
  const categoryLabel =
    category?.name ||
    (categoryLevel1
      ? `${categoryLevel1}`.replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase())
      : null);

  // Coarse until the viewer has inquired — the raw address is a street number
  // for providers who typed one, which identifies the company as plainly as the
  // name the card is already gating.
  const locationText = providerDisplayLocation({
    address: publicLocation?.address,
    country,
    canSeeFullName,
  });

  const listingFields = config.listing?.listingFields || [];
  // Form 1 §06 asks how many food businesses the provider has served as a
  // BAND, not a number — the old `jobsCompleted` integer is not on the form
  // and no longer exists, so the card shows the band's label instead.
  const clientsServedLabel = foodBusinessesServed
    ? labelForOption(listingFields, 'foodBusinessesServed', foodBusinessesServed)
    : null;
  const chipOptions = serviceAreas || services || [];
  const chipFieldKey = serviceAreas ? 'serviceAreas' : 'services';
  const chips = Array.isArray(chipOptions)
    ? chipOptions.map(o => labelForOption(listingFields, chipFieldKey, o))
    : [];

  // Ratings live in listing metadata (aggregated by an external process);
  // absent until that process exists, so the row simply doesn't render.
  const rating = typeof metadata.rating === 'number' ? metadata.rating : null;
  const reviewsCount = metadata.reviewsCount;

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : {};

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      title={title}
      {...setActivePropsMaybe}
    >
      <h3 className={css.title}>{title}</h3>

      {categoryLabel || locationText ? (
        <div className={css.badgeRow}>
          {categoryLabel ? <span className={css.categoryBadge}>{categoryLabel}</span> : null}
          {locationText ? (
            <span className={css.location}>
              <IconLocation className={css.locationIcon} />
              {locationText}
            </span>
          ) : null}
        </div>
      ) : null}

      {description ? <p className={css.description}>{description}</p> : null}

      {rating !== null ? (
        <div className={css.ratingRow}>
          <span
            className={css.stars}
            role="img"
            aria-label={intl.formatMessage({ id: 'ProviderCard.ratingLabel' }, { rating })}
          >
            {[0, 1, 2, 3, 4].map(i => {
              // Fractional fill: each star clips its gold overlay to the decimal
              // remainder so 4.6 and 4.9 stay visually distinct (whole-star
              // rounding collapsed every real-world rating to 5/5).
              const fillFraction = Math.max(0, Math.min(1, rating - i));
              return (
                <span key={i} className={css.starCell} aria-hidden="true">
                  <IconReviewStar className={css.star} />
                  <span className={css.starClip} style={{ width: `${fillFraction * 100}%` }}>
                    <IconReviewStar className={css.star} isFilled />
                  </span>
                </span>
              );
            })}
          </span>
          <span className={css.ratingText}>
            {reviewsCount != null ? `${rating} (${reviewsCount})` : rating}
          </span>
        </div>
      ) : null}

      {chips.length > 0 ? (
        <div className={css.chipRow}>
          {chips.map(chip => (
            <span key={chip} className={css.chip}>
              {chip}
            </span>
          ))}
        </div>
      ) : null}

      {establishedYear || clientsServedLabel ? (
        <div className={css.metaRow}>
          {establishedYear ? (
            <span className={css.metaItem}>
              <img className={css.metaIcon} src={medalIcon} alt="" />
              <FormattedMessage id="ProviderCard.est" values={{ year: establishedYear }} />
            </span>
          ) : null}
          {clientsServedLabel ? (
            <span className={css.metaItem}>
              <FormattedMessage
                id="ProviderCard.foodBusinessesServed"
                values={{ count: clientsServedLabel }}
              />
            </span>
          ) : null}
        </div>
      ) : null}
    </NamedLink>
  );
};

export default ProviderCard;
